import { Injectable, inject } from '@angular/core';

import { TODO } from '@b2b/types/core/utils';

import { CalendarService } from '../../calendar/calendar.service';
import { HelpersService } from '../../helpers/helpers.service';
import { FILTER_PORT, type IFilterPort } from '../../utils/filter.port';
import { TemplatesHelpersService } from '../helpers/templates-helpers.service';

/**
 * FlexpayService — Angular 18.
 *
 * Ported 1:1 from apps/b2b-ui/common/services/templates/flexpay/flexpay.service.js
 * (legacy AngularJS name 'FlexpayService', module 'acme.common.service.flexpay').
 *
 * Owns the flexpay schedule sub-model of a template (`model.flexPays`) and the
 * CRUD/format helpers around it. `$filter('date')` is bridged via FILTER_PORT
 * until the formatting layer is migrated.
 *
 * Registered back into AngularJS under 'FlexpayService' via downgradeInjectable
 * in app/upgrade/ng-downgraded-components.ts.
 */
@Injectable({ providedIn: 'root' })
export class FlexpayService {
  private templatesHelpers = inject(TemplatesHelpersService);
  private helpers = inject(HelpersService);
  private calendar = inject(CalendarService);
  private $filter = inject<IFilterPort>(FILTER_PORT);

  model: TODO = {};

  createHour = this.templatesHelpers.createHour;
  createRange = this.templatesHelpers.createRange;
  hourToDate = this.templatesHelpers.hourToDate;
  types = this.templatesHelpers.getFlexpayTypes();
  getFlexpayType = this.templatesHelpers.getFlexpayType;

  init(model: TODO): void {
    this.model = model;
  }

  createSchedule(scheduleStartTime?: TODO): TODO {
    const hour = this.createHour();
    const range = this.createRange();
    const sDate = scheduleStartTime ? new Date(scheduleStartTime) : this.hourToDate(hour);
    const startTime = sDate.setHours(12);
    const endTime = sDate.setHours(13);

    return {
      name: '',
      effectiveStartTime: this.calendar.toISO8601(startTime),
      effectiveEndTime: this.calendar.toISO8601(endTime),
      type: this.types[0].value,
      repeatCycle: 'never',
      weekdays: [],
      range: range,
      monthly: [],
      daysOfMonth: [],
      days: [this.calendar.toYMDDate(sDate)],
    };
  }

  hasSchedules(): TODO {
    return this.model.flexPays && this.model.flexPays instanceof Array && this.model.flexPays.length;
  }

  addSchedule(schedule: TODO): TODO {
    // All new schedules are unshifted to the array with a NEW property
    schedule.NEW = true;
    if (!this.model.flexPays || !(this.model.flexPays instanceof Array)) {
      this.model.flexPays = [];
    }
    this.model.flexPays.unshift(schedule);
    return schedule;
  }

  removeSchedule(index: number): void {
    if (this.hasSchedules()) {
      this.model.flexPays.splice(index, 1);
    }
  }

  getNewSchedule(): TODO {
    let r: TODO = false;

    if (this.hasSchedules()) {
      for (let i = 0, ii = this.model.flexPays.length; i < ii; i++) {
        if (this.model.flexPays[i].hasOwnProperty('NEW')) {
          r = { index: i, schedule: this.model.flexPays[i] };
          break;
        }
      }
    }

    if (!r) {
      // New schedule is unshifted so index is 0
      r = {};
      r.index = 0;
      r.schedule = this.addSchedule(this.createSchedule());
    }

    return r;
  }

  removeNewSchedule(): void {
    this.model.flexPays.splice(this.getNewSchedule().index, 1);
  }

  setSchedule(schedule: TODO): void {
    if (schedule && schedule.id) {
      this.model.flexPays[this.getScheduleById(schedule.id).index] = schedule;
    } else if (schedule.hasOwnProperty('NEW')) {
      this.model.flexPays[this.getNewSchedule().index] = schedule;
    } else {
      this.addSchedule(schedule);
    }
  }

  getSchedulesList(): TODO[] {
    let schedules: TODO[] = [];

    if (this.hasSchedules()) {
      schedules = this.processSchedules();
    }
    return schedules;
  }

  getScheduleById(id: TODO): TODO {
    let r: TODO = false;

    if (id && this.hasSchedules()) {
      for (let i = 0, ii = this.model.flexPays.length; i < ii; i++) {
        if (this.model.flexPays[i].id === id) {
          r = { index: i, schedule: this.model.flexPays[i] };
          break;
        }
      }
    }

    return r;
  }

  removeScheduleById(id: TODO): void {
    this.model.flexPays.splice(this.getScheduleById(id).index, 1);
  }

  getScheduleRangeHours(schedule: TODO): TODO[] {
    let range: TODO;
    const ranges: TODO[] = [];

    if (schedule && schedule.range) {
      ranges.push({
        start: this.hourToDate(schedule.range.start),
        end: this.hourToDate(schedule.range.end),
      });
    } else {
      range = this.createRange();
      ranges.push({
        start: this.hourToDate(range.start),
        end: this.hourToDate(range.end),
      });
    }

    return ranges;
  }

  processScheduleRange(range: TODO): string {
    let r = '';

    r += this.$filter('date')(this.hourToDate(range.start), this.helpers.dateFormats.standardTime);
    r += ' - ';
    r += this.$filter('date')(this.hourToDate(range.end), this.helpers.dateFormats.standardTime);

    return r;
  }

  processSchedules(): TODO[] {
    let effectiveDates: TODO;
    const schedules: TODO[] = [];

    for (let i = 0, ii = this.model.flexPays.length; i < ii; i++) {
      const flexpay = this.model.flexPays[i];
      // Legacy used moment(...).add(1, 'year').format() as a display-only fallback
      // when a flexpay has no effectiveEndTime. Replaced with native Date + the
      // migrated CalendarService to avoid a cross-package moment dependency.
      let endDate = flexpay.effectiveEndTime;
      if (!endDate) {
        const base = new Date(this.model.lastPublished || flexpay.effectiveStartTime);
        base.setFullYear(base.getFullYear() + 1);
        endDate = this.calendar.toISO8601(base);
      }

      effectiveDates = this.templatesHelpers.getEffectiveDates(flexpay.effectiveStartTime, endDate);
      schedules.push({
        id: flexpay.id,
        name: flexpay.name,
        type: this.getFlexpayType(this.types, flexpay.type).label,
        hours: this.processScheduleRange(flexpay.range),
        repeatCycle: this.templatesHelpers.getRepeatType(
          this.templatesHelpers.getRepeatTypes(),
          this.model.flexPays[i].repeatCycle,
        ).label,
        effectiveDates: effectiveDates,
      });
    }

    return schedules;
  }

  verifySchedules(): void {
    if (this.hasSchedules()) {
      // Migrate start dates to all flexPays
      for (let i = 0, ii = this.model.flexPays.length; i < ii; i++) {
        // Copy temporary weekday values
        if (this.model.flexPays[i].repeatCycle === 'month') {
          if (this.model.flexPays[i].daysOfMonth instanceof Array && this.model.flexPays[i].daysOfMonth.length > 0) {
            this.model.flexPays[i].monthly = null;
            delete this.model.flexPays[i].monthly;

            this.model.flexPays[i].weekdays = null;
            delete this.model.flexPays[i].weekdays;
          } else if (this.model.flexPays[i].monthly instanceof Array && this.model.flexPays[i].monthly.length > 0) {
            this.model.flexPays[i].daysOfMonth = null;
            delete this.model.flexPays[i].daysOfMonth;
          }
        } else {
          this.model.flexPays[i].monthly = null;
          delete this.model.flexPays[i].monthly;

          this.model.flexPays[i].daysOfMonth = null;
          delete this.model.flexPays[i].daysOfMonth;

          // Include a days object if event never repeats
          if (this.model.flexPays[i].repeatCycle === 'never') {
            this.model.flexPays[i].days = [
              this.calendar.toYMDDate(this.calendar.parseDate(this.model.flexPays[i].effectiveStartTime)),
            ];
          } else {
            this.model.flexPays[i].days = null;
            delete this.model.flexPays[i].days;
          }
        }
      }

      this.verifyScheduleRanges();
    }
  }

  verifyScheduleRanges(): void {
    for (let i = 0, ii = this.model.flexPays.length; i < ii; i++) {
      if (!this.model.flexPays[i].range) {
        this.model.flexPays[i].ranges = this.createRange();
      }
    }
  }
}
